/**
 * HOOK GESTION PAIEMENTS CLIENTS LOGISTICS
 * =======================================
 * Hook custom pour la gestion des paiements clients (encaissements)
 *
 * Workflow:
 * 1. Afficher l'historique des paiements d'une facture client
 * 2. Enregistrer un nouveau paiement (partiel ou complet)
 * 3. Suppression d'un paiement avec son droit dédié
 * 4. Recalcul du balance_due et payment_status dans la transaction backend
 *
 * Backend:
 * - GET /api/logistics/customer-payments?invoice_id=X
 * - POST /api/logistics/customer-payments (creation)
 * - DELETE /api/logistics/customer-payments/:id (suppression)
 *
 * Les anciens triggers DB restent compatibles avec le service partagé.
 *
 * @see routes/logistics/customer-payments.js
 * @see src/gestion-logistics/types/payment.types.ts
 */

import { useState, useCallback } from 'react';
import apiClient from '../../api/apiClient';
import { message } from 'antd';
import API_CONFIG from '../../config/api';
import { CustomerPayment, CustomerPaymentFormData } from '../types/payment.types';
import { useSelection } from '../../SelectionContext';

export const useCustomerPayments = () => {
  const [loading, setLoading] = useState(false);
  const [customerPayments, setCustomerPayments] = useState<CustomerPayment[]>([]);
  const [currentPayment, setCurrentPayment] = useState<CustomerPayment | null>(null);

  const { selectedCompany } = useSelection();

  /**
   * Recuperer tous les paiements d'une facture client specifique
   * @param invoiceId - ID de la facture client
   */
  const fetchPaymentsByInvoice = useCallback(async (invoiceId: number) => {
    setLoading(true);
    try {
      const response = await apiClient.get(
        `${API_CONFIG.API_URL}/logistics/customer-payments`,
        {
          params: { invoice_id: invoiceId },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );

      if (response.data.success) {
        const paymentsList = response.data.data || [];
        setCustomerPayments(paymentsList);
        console.log('[useCustomerPayments] Paiements charges:', paymentsList.length);
        return paymentsList;
      }

      message.error(response.data.message || 'Erreur lors du chargement des paiements');
      return [];
    } catch (error: any) {
      console.error('[useCustomerPayments] Erreur fetchPaymentsByInvoice:', error);
      message.error(error.response?.data?.message || 'Erreur de connexion au serveur');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Recuperer les details d'un paiement specifique
   * @param paymentId - ID du paiement
   */
  const fetchPaymentById = useCallback(async (paymentId: number): Promise<CustomerPayment | null> => {
    setLoading(true);
    try {
      const response = await apiClient.get(
        `${API_CONFIG.API_URL}/logistics/customer-payments/${paymentId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );

      if (response.data.success) {
        const payment = response.data.data;
        setCurrentPayment(payment);
        console.log('[useCustomerPayments] Paiement charge:', payment.payment_reference);
        return payment;
      }

      message.error(response.data.message || 'Erreur lors du chargement du paiement');
      return null;
    } catch (error: any) {
      console.error('[useCustomerPayments] Erreur fetchPaymentById:', error);
      message.error(error.response?.data?.message || 'Erreur de connexion');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Enregistrer un nouveau paiement client
   *
   * Validations automatiques backend:
   * - Montant ne doit pas depasser balance_due
   * - Facture doit exister et appartenir a la company
   * - Transaction atomique avec mise a jour auto via trigger
   *
   * @param data - Donnees du paiement
   */
  const recordPayment = useCallback(async (
    data: CustomerPaymentFormData
  ): Promise<CustomerPayment | null> => {
    setLoading(true);
    try {
      console.log('[useCustomerPayments] Enregistrement paiement:', data);

      const response = await apiClient.post(
        `${API_CONFIG.API_URL}/logistics/customer-payments`,
        {
          ...data,
          company_id: selectedCompany, // SuperAdmin context
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        const newPayment = response.data.data;
        message.success(response.data.message || 'Paiement enregistre avec succes');

        // Ajouter le nouveau paiement a la liste
        setCustomerPayments((prev) => [newPayment, ...prev]);
        setCurrentPayment(newPayment);

        console.log('[useCustomerPayments] Paiement cree:', newPayment.amount);
        return newPayment;
      }

      message.error(response.data.message || 'Erreur lors de l\'enregistrement');
      return null;
    } catch (error: any) {
      console.error('[useCustomerPayments] Erreur recordPayment:', error);
      message.error(
        error.response?.data?.message || 'Erreur lors de l\'enregistrement du paiement'
      );
      return null;
    } finally {
      setLoading(false);
    }
  }, [selectedCompany]);

  /**
   * Supprimer un paiement client
   * IMPORTANT: Recalcul automatique via trigger DB
   *
   * @param paymentId - ID du paiement a supprimer
   */
  const deletePayment = useCallback(async (paymentId: number): Promise<boolean> => {
    setLoading(true);
    try {
      console.log('[useCustomerPayments] Suppression paiement:', paymentId);

      const response = await apiClient.delete(
        `${API_CONFIG.API_URL}/logistics/customer-payments/${paymentId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );

      if (response.data.success) {
        message.success('Paiement supprime avec succes');

        // Retirer le paiement de la liste
        setCustomerPayments((prev) => prev.filter((payment) => payment.id !== paymentId));

        console.log('[useCustomerPayments] Paiement supprime (balance recalcule via trigger)');
        return true;
      }

      message.error(response.data.message || 'Erreur lors de la suppression');
      return false;
    } catch (error: any) {
      console.error('[useCustomerPayments] Erreur deletePayment:', error);
      message.error(error.response?.data?.message || 'Erreur lors de la suppression');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // Etat
    loading,
    customerPayments,
    currentPayment,

    // Actions
    fetchPaymentsByInvoice,
    fetchPaymentById,
    recordPayment,
    deletePayment,
  };
};

export default useCustomerPayments;
